import { useEffect, useState } from "react";
import api, { errMsg } from "../lib/api";
import { ROLES, ROLE_LABELS, MANAGER_ROLES, fmtDate } from "../lib/constants";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { ActionsMenu } from "../components/ActionsMenu";
import { ConfirmAction } from "../components/ConfirmAction";

const empty = { name: "", email: "", password: "", phone: "", role: "sales_consultant", manager_id: "", view_all_leads: false, send_from_email: "" };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty);
  const [mailboxes, setMailboxes] = useState([]);
  const [mbAvailable, setMbAvailable] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/mailboxes").then((r) => { setMbAvailable(!!r.data.available); setMailboxes(r.data.mailboxes || []); }).catch(() => {});
  }, []);

  const managers = users.filter((u) => MANAGER_ROLES.includes(u.role));
  const userName = (id) => users.find((u) => u.id === id)?.name || "—";

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/users", { ...form, manager_id: form.manager_id || null, send_from_email: (form.send_from_email || "").trim().toLowerCase() }); toast.success("User created"); setShow(false); setForm(empty); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => { await api.delete(`/users/${id}`); load(); toast.success("Deleted"); };
  const updateField = async (id, patch) => { try { await api.put(`/users/${id}`, patch); load(); toast.success("Updated"); } catch (e) { toast.error(errMsg(e)); } };
  const [testing, setTesting] = useState("");
  const [editing, setEditing] = useState(null);
  const setEd = (patch) => setEditing((prev) => ({ ...prev, ...patch }));
  const saveEdit = async (e) => {
    e.preventDefault();
    const p = {
      name: editing.name,
      phone: editing.phone || "",
      send_from_email: (editing.send_from_email || "").trim().toLowerCase(),
      manager_id: editing.manager_id || "",
      view_all_leads: !!editing.view_all_leads,
      active: editing.active !== false,
    };
    if (editing.role !== "super_admin") p.role = editing.role;
    if (editing.password) p.password = editing.password;
    try { await api.put(`/users/${editing.id}`, p); toast.success("User updated"); setEditing(null); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const testMailbox = async (id) => {
    setTesting(id);
    try {
      const { data } = await api.post(`/users/${id}/test-email`);
      if (data.ok) toast.success(`Test sent from ${data.mailbox} — check ${data.sent_to}`, { duration: 6000 });
      else toast.error(`Not authorised (${data.mailbox}): ${data.detail}`, { duration: 9000 });
    } catch (e) { toast.error(errMsg(e)); } finally { setTesting(""); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-3xl font-head font-extrabold tracking-tight">Users</h1>
          <p className="text-muted-foreground">Create team members, assign roles & reporting lines</p></div>
        <button data-testid="new-user-btn" onClick={() => setShow(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground font-semibold hover:-translate-y-[1px] transition-all">
          <Icons.UserPlus size={18} /> Add User
        </button>
      </div>

      <div className="bg-card border border-border rounded-md shadow-sm overflow-x-auto">
        {!mbAvailable && (
          <div className="flex items-start gap-2.5 text-xs text-muted-foreground bg-muted/40 border-b border-border p-3" data-testid="mailbox-manual-note">
            <Icons.Info size={15} className="text-primary mt-0.5 shrink-0" />
            <span>Assign each person's Outlook mailbox in the <b>Send-from Mailbox</b> column — type the address and press Enter (or the check). Emails they send (quotes, assignment) go from that mailbox; the rest use the company default. <b>Tip:</b> to get an automatic drop-down of your Microsoft 365 mailboxes, grant the app the <b>User.Read.All</b> permission in Azure AD.</span>
          </div>
        )}
        <table className="crm-compact-table w-full text-sm" data-testid="users-table">
          <thead><tr className="text-left label-caps border-b border-border">
            <th className="p-3">Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Send-from Mailbox</th><th>Reports To</th><th>Lead Access</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {users.map((u) => {
              const isConsultant = ["sales_consultant", "admin_consultant"].includes(u.role);
              return (
                <tr key={u.id} className="border-b border-border/50">
                  <td className="p-3 font-medium">{u.name}<div className="xl:hidden text-xs text-muted-foreground font-normal break-all">{u.email} · {ROLE_LABELS[u.role]}</div></td>
                  <td className="text-muted-foreground">{u.email}</td>
                  <td>{u.phone || "-"}</td>
                  <td><span className="text-xs font-semibold px-2 py-0.5 rounded bg-secondary/10 text-secondary">{ROLE_LABELS[u.role]}</span></td>
                  <td>
                    <MailboxCell user={u} available={mbAvailable} mailboxes={mailboxes}
                      busy={testing === u.id} onTest={() => testMailbox(u.id)}
                      onSave={(v) => updateField(u.id, { send_from_email: v })} />
                  </td>
                  <td>
                    {isConsultant ? (
                      <select value={u.manager_id || ""} onChange={(e) => updateField(u.id, { manager_id: e.target.value || "" })}
                        data-testid={`manager-select-${u.id}`} className="text-xs px-2 py-1 rounded border border-input bg-background max-w-[140px]">
                        <option value="">— None —</option>{managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
                    ) : <span className="text-xs text-muted-foreground">{u.manager_id ? userName(u.manager_id) : "-"}</span>}
                  </td>
                  <td>
                    {u.role === "admin_consultant" ? (
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input type="checkbox" checked={!!u.view_all_leads} onChange={(e) => updateField(u.id, { view_all_leads: e.target.checked })}
                          data-testid={`viewall-${u.id}`} /> See all leads
                      </label>
                    ) : MANAGER_ROLES.includes(u.role) ? <span className="text-xs text-muted-foreground">Team + unassigned</span> : <span className="text-xs text-muted-foreground">Assigned only</span>}
                  </td>
                  <td className="text-xs text-muted-foreground">{fmtDate(u.created_at)}{u.active === false && <span className="ml-1 px-1.5 py-0.5 rounded bg-red-100 text-red-600 font-semibold">Disabled</span>}</td>
                  <td>
                    <ActionsMenu id={`user-actions-${u.id}`} compact label={`Actions for ${u.name}`} items={[
                      { id: `edit-user-${u.id}`, icon: "Pencil", label: "Edit user", onSelect: () => setEditing({ ...u, password: "" }) },
                      { id: `delete-user-${u.id}`, icon: "Trash2", label: "Delete user", hidden: u.role === "super_admin", destructive: true,
                        onSelect: () => setConfirm({ title: "Delete user?", description: `${u.name} will no longer be able to access this CRM.`, label: "Delete user", run: () => del(u.id) }) },
                    ]} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfirmAction action={confirm} onClose={() => setConfirm(null)} />
      {show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="bg-card rounded-md w-full max-w-md p-6 shadow-lg max-h-[90vh] overflow-y-auto" data-testid="user-form">
            <h2 className="text-xl font-head font-extrabold mb-4">Add User</h2>
            {[["name", "Name", "text"], ["email", "Email", "email"], ["password", "Password", "password"], ["phone", "Phone", "text"]].map(([k, l, t]) => (
              <div key={k} className="mb-3"><label className="label-caps">{l}</label>
                <input required={k !== "phone"} type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`user-${k}`} className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
            ))}
            <label className="label-caps">Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="user-role" className="w-full mt-1 mb-4 px-3 py-2 rounded-md border border-input bg-background">
              {ROLES.filter((r) => r !== "super_admin").map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
            <div className="mb-4"><label className="label-caps">Send-from email (their M365 mailbox)</label>
              <MailboxField value={form.send_from_email} onChange={(v) => setForm({ ...form, send_from_email: v })}
                available={mbAvailable} mailboxes={mailboxes} testid="user-sendfrom"
                className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" />
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {mbAvailable
                  ? "Pick this person's Outlook mailbox. Their quote & assignment emails are sent from it. You can change it anytime from the table."
                  : "Type this person's Outlook mailbox. Their quote & assignment emails are sent from it. Leave blank for the company default. You can change it anytime from the table."}
              </p></div>
            {["sales_consultant", "admin_consultant"].includes(form.role) && (
              <div className="mb-4"><label className="label-caps">Reports To (Manager)</label>
                <select value={form.manager_id} onChange={(e) => setForm({ ...form, manager_id: e.target.value })}
                  data-testid="user-manager" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                  <option value="">— None —</option>{managers.map((m) => <option key={m.id} value={m.id}>{m.name} ({ROLE_LABELS[m.role]})</option>)}</select></div>
            )}
            {form.role === "admin_consultant" && (
              <label className="flex items-center gap-2 mb-4 text-sm cursor-pointer">
                <input type="checkbox" checked={form.view_all_leads} onChange={(e) => setForm({ ...form, view_all_leads: e.target.checked })} data-testid="user-viewall" />
                Allow this Admin Consultant to see all leads
              </label>
            )}
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 rounded-md border border-border">Cancel</button>
              <button type="submit" className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold" data-testid="user-submit">Create</button>
            </div>
          </form>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={saveEdit} className="bg-card rounded-md w-full max-w-md p-6 shadow-lg max-h-[90vh] overflow-y-auto" data-testid="user-edit-form">
            <h2 className="text-xl font-head font-extrabold mb-1">Edit User</h2>
            <p className="text-xs text-muted-foreground mb-4">{editing.email}</p>
            <div className="mb-3"><label className="label-caps">Name</label>
              <input required value={editing.name || ""} onChange={(e) => setEd({ name: e.target.value })}
                data-testid="edit-name" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
            <div className="mb-3"><label className="label-caps">Phone</label>
              <input value={editing.phone || ""} onChange={(e) => setEd({ phone: e.target.value })}
                data-testid="edit-phone" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
            <div className="mb-3"><label className="label-caps">Role</label>
              {editing.role === "super_admin" ? (
                <div className="mt-1 px-3 py-2 rounded-md border border-input bg-muted/40 text-sm text-muted-foreground">{ROLE_LABELS.super_admin} (locked)</div>
              ) : (
                <select value={editing.role} onChange={(e) => setEd({ role: e.target.value })} data-testid="edit-role"
                  className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                  {ROLES.filter((r) => r !== "super_admin").map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </select>
              )}</div>
            <div className="mb-3"><label className="label-caps">Reset password (leave blank to keep)</label>
              <input type="password" value={editing.password || ""} onChange={(e) => setEd({ password: e.target.value })}
                data-testid="edit-password" placeholder="••••••••" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
            <div className="mb-3"><label className="label-caps">Send-from mailbox</label>
              <MailboxField value={editing.send_from_email} onChange={(v) => setEd({ send_from_email: v })}
                available={mbAvailable} mailboxes={mailboxes} testid="edit-sendfrom"
                className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background" /></div>
            {["sales_consultant", "admin_consultant"].includes(editing.role) && (
              <div className="mb-3"><label className="label-caps">Reports To (Manager)</label>
                <select value={editing.manager_id || ""} onChange={(e) => setEd({ manager_id: e.target.value })}
                  data-testid="edit-manager" className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background">
                  <option value="">— None —</option>{managers.filter((m) => m.id !== editing.id).map((m) => <option key={m.id} value={m.id}>{m.name} ({ROLE_LABELS[m.role]})</option>)}</select></div>
            )}
            {editing.role === "admin_consultant" && (
              <label className="flex items-center gap-2 mb-3 text-sm cursor-pointer">
                <input type="checkbox" checked={!!editing.view_all_leads} onChange={(e) => setEd({ view_all_leads: e.target.checked })} data-testid="edit-viewall" />
                Allow this Admin Consultant to see all leads
              </label>
            )}
            {editing.role !== "super_admin" && (
              <label className="flex items-center gap-2 mb-4 text-sm cursor-pointer">
                <input type="checkbox" checked={editing.active !== false} onChange={(e) => setEd({ active: e.target.checked })} data-testid="edit-active" />
                Active (uncheck to disable login)
              </label>
            )}
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-border">Cancel</button>
              <button type="submit" className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-semibold" data-testid="edit-submit">Save Changes</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function MailboxField({ value, onChange, available, mailboxes, testid, className }) {
  if (available && mailboxes.length) {
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={className}>
        <option value="">Company default</option>
        {mailboxes.map((m) => <option key={m.email} value={m.email}>{m.name} ({m.email})</option>)}
      </select>
    );
  }
  return (
    <input type="email" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      placeholder="e.g. archer@griansolarpower.au" className={className} />
  );
}

function MailboxCell({ user, available, mailboxes, onSave, onTest, busy }) {
  const [val, setVal] = useState(user.send_from_email || "");
  useEffect(() => { setVal(user.send_from_email || ""); }, [user.send_from_email]);
  const dirty = (val || "").trim().toLowerCase() !== (user.send_from_email || "");
  const testBtn = (
    <button data-testid={`mailbox-test-${user.id}`} onClick={onTest} disabled={busy} title="Send a test email from this mailbox"
      className="text-secondary hover:text-secondary/80 disabled:opacity-40 shrink-0">
      {busy ? <Icons.LoaderCircle size={15} className="animate-spin" /> : <Icons.Send size={15} />}
    </button>
  );
  if (available && mailboxes.length) {
    return (
      <div className="flex items-center gap-1.5">
        <select value={user.send_from_email || ""} data-testid={`mailbox-${user.id}`} onChange={(e) => onSave(e.target.value)}
          className="text-xs px-2 py-1 rounded border border-input bg-background max-w-[180px]">
          <option value="">Company default</option>
          {mailboxes.map((m) => <option key={m.email} value={m.email}>{m.email}</option>)}
        </select>
        {testBtn}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <input type="email" value={val} data-testid={`mailbox-${user.id}`} onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") onSave(val.trim().toLowerCase()); }}
        placeholder="company default" className="text-xs px-2 py-1 rounded border border-input bg-background w-[150px]" />
      {dirty && <button data-testid={`mailbox-save-${user.id}`} onClick={() => onSave(val.trim().toLowerCase())} title="Save mailbox" className="text-primary shrink-0"><Icons.Check size={15} /></button>}
      {testBtn}
    </div>
  );
}
